import React, { useEffect, useRef, useState } from "react";
import { SearchResult } from "../../../types/navbarTypes";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { getID } from "../../../store/slices/IdSlice";
const baseUrl = import.meta.env.VITE_API_URL;

const DesktopSearch = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isFocused, setIsFocused] = useState(false);
  const [search, setSearch] = useState<string>();
  const [text, setText] = useState("");
  const [isOpen, setIsOpen] = useState(true);
  const dropdownRef = useRef<HTMLUListElement | null>(null);
  const [searchApiData, setSearchApiData] = useState<SearchResult[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const categories = [
    "Visit Visa",
    "Spouse Visa",
    "Golden Visa",
    "Family Visa",
    "Employment Visa",
    "Newborn Visa",
  ];

  useEffect(() => {
    if (isFocused) return; // Stop animation when focused

    const currentCategory = categories[currentIndex];

    const typingSpeed = 40;
    const deletingSpeed = 40;
    const pauseDuration = 1000; // Pause after a full text is displayed

    let timeout;

    if (isDeleting) {
      timeout = setTimeout(() => {
        setText((prev) => prev.slice(0, -1));
        if (text === "") {
          setIsDeleting(false);
          setCurrentIndex((prevIndex) => (prevIndex + 1) % categories.length);
        }
      }, deletingSpeed);
    } else {
      timeout = setTimeout(() => {
        setText((prev) => currentCategory.slice(0, prev.length + 1));
        if (text === currentCategory) {
          setTimeout(() => setIsDeleting(true), pauseDuration);
        }
      }, typingSpeed);
    }

    return () => clearTimeout(timeout);
  }, [text, isDeleting, currentIndex, categories, isFocused]);

  useEffect(() => {
    if (search?.trim() === "") {
      setIsOpen(false);
    }
  }, [search]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchValue = e.target.value;
    setSearch(newSearchValue); // Update the search state
    fetchSearchResults(newSearchValue); // Call the API with the updated search term

    if (newSearchValue.trim() !== "") {
      setIsOpen(true);
    }
  };

  const fetchSearchResults = (searchTerm: string) => {
    if (searchTerm.trim() === "") return; // Avoid calling the API if the search term is empty
    fetch(`${baseUrl}/api/openRoute/search?searchTerm=${searchTerm}`)
      .then((response) => response.json())
      .then((data) => {
        setSearchApiData(data.results); // Handle the API response data here
      })
      .catch((error) => {
        console.error(
          "Error fetching search results ::: fetchSearchResults",
          error
        );
      });
  };

  const handleSuggestionClick = (
    insideId: string,
    outsideId: string,
    slug: string
  ) => {
    dispatch(getID({ insideId, outsideId }));
    if (location.pathname.startsWith("/services/")) {
      navigate(`/services/${slug}`, { state: { action: "toggleVisibility" } });
    } else {
      navigate(`/services/${slug}`, { state: { action: "toggleVisibility" } });
    }
    setSearch("");
    setIsOpen(false);
  };

  return (
    <>
      <div className="search-container position-relative col-6">
        <input
          type="text"
          className="form-control"
          placeholder={isFocused ? "" : `Search for ${text}`}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          value={search}
          onChange={handleSearchChange}
          aria-label="Search"
        />
        <div className="search-icon position-absolute top-50 translate-middle-y end-0 me-3">
          <i className="fas fa-search"></i>
        </div>

        {isOpen && searchApiData.length > 0 && (
          <ul
            id="dropdownList"
            ref={dropdownRef}
            className="dv_search_showing_services_ul dv_search_desktop_drp list-group position-absolute w-100"
          >
            {searchApiData.map((suggestion, index) => (
              <li key={index} className="list-group-item">
                <a
                  onClick={() =>
                    handleSuggestionClick(
                      suggestion?.serviceNameId,
                      suggestion?.sub2RecordId,
                      suggestion?.sub2SlugServiceSub1
                    )
                  }
                >
                  <div className="dv_service_img_wrap_search d-flex">
                    <img
                      loading="lazy"
                      src={suggestion.imageSrc}
                      alt={suggestion.serviceName}
                      className="me-3"
                    />
                    <div className="dv_service_img_txt_search">
                      <h4>
                        {suggestion.serviceName}
                        {/* {suggestion?.serviceNameId} 
                         {suggestion?.sub2RecordId} */}
                      </h4>
                      <div className="dv_subscategory_per_service_rating gap-2 d-flex align-items-center">
                        {suggestion.prices.length > 1 ? (
                          <>
                            <div
                              className="dv_search_service_price"
                              key="lowest-price"
                              style={{ fontSize: "12px" }}
                            >
                              Start from -
                              <span>
                                {Math.min(
                                  ...suggestion.prices.map(
                                    (price) => price.PriceAmount
                                  )
                                )}
                                {suggestion.prices[0].PriceCurrency}{" "}
                              </span>
                            </div>
                          </>
                        ) : (
                          suggestion.prices.map((price, index) => (
                            <div
                              className="dv_search_service_price"
                              key={price.PriceAmount + 1}
                              style={{ fontSize: "12px" }}
                            >
                              {price.PriceType} -
                              <span>
                                {price.PriceAmount}
                                {price.PriceCurrency}
                              </span>
                              {index < suggestion.prices.length - 1 && (
                                <span> / </span>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
};

export default DesktopSearch;
